<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { Locale, SelectFieldConfig } from '@/forms/types'

const props = defineProps<{
  field: SelectFieldConfig
  locale: Locale
  modelValue: string
  error?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const { t } = useI18n()
const errorId = `${props.field.id}-error`
const hintId = `${props.field.id}-hint`
</script>

<template>
  <div class="field" :class="{ 'has-error': !!error }">
    <label class="field-label" :for="field.id">
      {{ field.label[locale] }}
      <span v-if="field.required" aria-hidden="true" class="req">*</span>
    </label>
    <p v-if="field.description" :id="hintId" class="field-hint">{{ field.description[locale] }}</p>
    <select
      :id="field.id"
      class="field-input"
      :value="modelValue"
      :required="field.required"
      :aria-required="field.required ? 'true' : undefined"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="
        [error ? errorId : '', field.description ? hintId : ''].filter(Boolean).join(' ') ||
        undefined
      "
      @change="emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
    >
      <option value="" disabled>{{ t('select.placeholder') }}</option>
      <option v-for="opt in field.options" :key="opt.value" :value="opt.value">
        {{ opt.label[locale] }}
      </option>
    </select>
    <p v-if="error" :id="errorId" class="field-error" role="alert">{{ error }}</p>
  </div>
</template>

<style scoped>
.field {
  margin-bottom: var(--space-5);
}
select.field-input {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%23666' stroke-width='1.5' d='M1 1.5 6 6.5 11 1.5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right var(--space-3) center;
  padding-right: var(--space-7);
  cursor: pointer;
}
</style>
